import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import VerifiedIcon from "@mui/icons-material/Verified";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  downloadPrescriptionPdf,
  getPrescription,
  PRESCRIPTION_QUERY_KEY,
} from "@/api/appointmentApi";
import type { Prescription } from "@/types/domain";

interface Props {
  appointmentId: string;
}

export default function PrescriptionViewCard({ appointmentId }: Props) {
  const { data: prescription, isLoading } = useQuery({
    queryKey: PRESCRIPTION_QUERY_KEY(appointmentId),
    queryFn: () => getPrescription(appointmentId),
    retry: false,
  });

  const downloadMut = useMutation({
    mutationFn: () =>
      downloadPrescriptionPdf(
        appointmentId,
        `prescription-${prescription?.verificationCode ?? appointmentId}.pdf`,
      ),
  });

  if (isLoading) return null;
  if (!prescription) return null;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        mt: 2,
        borderColor: "success.light",
        bgcolor: "success.50",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <VerifiedIcon color="success" fontSize="small" />
          <Typography variant="subtitle1" fontWeight={700}>
            Prescription
          </Typography>
          <Chip
            label={prescription.verificationCode}
            size="small"
            color="success"
            variant="outlined"
          />
        </Stack>
        <Button
          startIcon={<DownloadIcon />}
          variant="contained"
          size="small"
          color="success"
          disabled={downloadMut.isPending}
          onClick={() => downloadMut.mutate()}
          sx={{ mt: { xs: 1, sm: 0 } }}
        >
          {downloadMut.isPending ? "Downloading…" : "Download PDF"}
        </Button>
      </Stack>

      {prescription.diagnosis && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary">
            DIAGNOSIS
          </Typography>
          <Typography variant="body2">{prescription.diagnosis}</Typography>
        </Box>
      )}

      {prescription.medications && prescription.medications.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="caption"
            fontWeight={700}
            color="text.secondary"
            sx={{ mb: 0.5, display: "block" }}
          >
            MEDICATIONS
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Drug</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Dosage</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Frequency</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Duration</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Instructions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {prescription.medications.map(
                  (med: Prescription["medications"][number], i: number) => (
                    <TableRow key={med.id ?? i}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{med.drugName}</TableCell>
                      <TableCell>{med.dosage ?? "—"}</TableCell>
                      <TableCell>{med.frequency ?? "—"}</TableCell>
                      <TableCell>{med.duration ?? "—"}</TableCell>
                      <TableCell>{med.instructions ?? "—"}</TableCell>
                    </TableRow>
                  ),
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {prescription.diagnosticReferral && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary">
            DIAGNOSTIC REFERRAL
          </Typography>
          <Typography variant="body2">
            {prescription.diagnosticReferral}
          </Typography>
        </Box>
      )}

      {prescription.remarks && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary">
            REMARKS
          </Typography>
          <Typography variant="body2">{prescription.remarks}</Typography>
        </Box>
      )}

      <Divider sx={{ my: 1.5 }} />
      <Typography variant="caption" color="text.secondary">
        This prescription was issued via ChikitsaLink Telemedicine.
        Verification ID: {prescription.verificationCode}
      </Typography>
    </Paper>
  );
}
